/* eslint-disable no-param-reassign */
import _ from 'lodash';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { MLProductCodeUtil } from 'ml-portals-util-js';
import MultiLobs from '../models/MultiLobs';
import MLPortalStorageService from './MLPortalStorageService';

const {
    ML_STORAGE_KEY_BUNDLE_DATA,
    HVQConsumerSteps
} = appConfig;


let multiLobsData = new MultiLobs();

const paGpaPurchaseFlowSteps = ['paAddtlInfoToBuy', 'paDisclosureReviewToBuy', 'paVeracityCheck'];
const hoGpaPurchaseFlowSteps = ['hoAddtlInfoToBuy'];
const pupGpaPurchaseFlowSteps = ['pupAddtlDetails', 'pupDisclosureReviewToBuy'];
const commonGpaPurchaseFlowSteps = ['paymentDetails', 'policySummary', 'bindingScripts', 'verbalAuthorization',
    'verbalAuthorization', 'requiredSignatures', 'availableDocuments', 'confirmation'];

function reset() {
    multiLobsData = new MultiLobs();
}

function addToCompletedSteps(stepName, job) {
    job.completedSteps = job.completedSteps || [];
    job.completedSteps = new Set(job.completedSteps);
    job.completedSteps.add(stepName);
    job.completedSteps = [...job.completedSteps];
}

function getJobDataFromSubmission(submission) {
    const { baseData, lobData } = submission;

    const isHoCoveragePart = lobData
        && lobData.homeowners
        && MLProductCodeUtil.isHomeOwnersCoveragePart(lobData.homeowners.coverables.coveragePartType_ML);

    const productCode = isHoCoveragePart
        ? MLProductCodeUtil.getHomeOwnersCode()
        : baseData.productCode;

    const jobData = multiLobsData.createJob({
        jobNumber: submission.quoteID,
        productCode: productCode,
        coveragePartType: isHoCoveragePart ? lobData.homeowners.coverables.coveragePartType_ML : null,
        periodStart: baseData.periodStartDate,
        stateCode_ML: baseData.policyAddress.state,
        postalCode: baseData.policyAddress.postalCode,
        accountNumber: baseData.accountNumber
    });

    return multiLobsData.createJob(jobData);
}

export default {
    nextJob() {
        return multiLobsData.nextJob();
    },

    startJob(accountNumber, quoteID, productCode, postalCode, additionalHeader = {}) {
        multiLobsData.accountNumber = accountNumber;
        const aJob = this.nextJob();
        if (aJob) {
            aJob.jobNumber = quoteID;
            aJob.productCode = productCode;
            aJob.postalCode = postalCode;
            this.syncToPortalStorage(additionalHeader);
        }
    },

    updateJob(quoteID, productCode, postalCode, coveragePartType, additionalHeaders = {}) {
        const aJob = this.nextJob();
        if (aJob) {
            aJob.jobNumber = quoteID;
            aJob.productCode = productCode;
            aJob.postalCode = postalCode;
            if (!_.isNil(coveragePartType)) {
                aJob.coveragePartType = coveragePartType;
            }
            this.syncToPortalStorage(additionalHeaders);
        }
    },

    syncToPortalStorage(additionalHeaders = {}) {
        if (multiLobsData.accountNumber) {
            if (multiLobsData.hasMultiJobs()) {
                MLPortalStorageService.store(null, multiLobsData.accountNumber, ML_STORAGE_KEY_BUNDLE_DATA, JSON.stringify(multiLobsData), additionalHeaders);
            } else if (multiLobsData.hasSingleJob()) {
                MLPortalStorageService.store(multiLobsData.retrieveSingleJob().jobNumber, multiLobsData.accountNumber, ML_STORAGE_KEY_BUNDLE_DATA, JSON.stringify(multiLobsData), additionalHeaders);
            }
        }
    },

    storeFlowSteps(fromStep, nextStep, additionalHeaders = {}) {
        const current = multiLobsData.nextJob();
        if (current) {
            current.lastVisitedStep = nextStep;
            addToCompletedSteps(fromStep, current);
            this.syncToPortalStorage(additionalHeaders);
            console.log(`Updating lastVisitedStep to ${current.lastVisitedStep} marking ${fromStep} as completed`);
        }
    },

    storeFlowStepsBulk(steps, additionalHeaders = {}) {
        const current = multiLobsData.nextJob();

        if (current) {
            steps.forEach(step => addToCompletedSteps(step, current));
            this.syncToPortalStorage(additionalHeaders);
        }
    },

    getLastVisitedStep() {
        const current = multiLobsData.nextJob();
        return _.get(current, 'lastVisitedStep');
    },

    retrieveFromPortalStorage(accountNumber, jobNumber,  additionalHeaders = {}) {
        const promise = MLPortalStorageService.retrieve(jobNumber, accountNumber, ML_STORAGE_KEY_BUNDLE_DATA, additionalHeaders);

        promise.then((data) => {
            if (data && !_.isEmpty(data)) {
                multiLobsData = new MultiLobs(JSON.parse(data));
            }
        });
        return promise;
    },

    hasNext() {
        return multiLobsData.hasNext();
    },

    addJob(jobData, additionalHeader = {}) {
        const aJob = multiLobsData.createJob(jobData);
        multiLobsData.addJob(aJob);
        this.syncToPortalStorage(additionalHeader);
        return aJob;
    },

    getJobDataFromSubmission,

    addJobFromSubmission(submission, additionalHeader = {}) {
        const jobData = this.getJobDataFromSubmission(submission);
        this.addJob(jobData, additionalHeader);
    },

    reset,

    getFirstHVQConsumerNonCompletedStep() {
        const nextJob = multiLobsData.nextJob();
        if (nextJob) {
            let completedSteps = multiLobsData.nextJob().completedSteps || [];
            completedSteps = new Set(completedSteps);
            return [...HVQConsumerSteps].find((hvqConsumerStep) => !completedSteps.has(hvqConsumerStep));
        }
        return null;
    },

    hasCompletedStep(stepName) {
        const current = multiLobsData.nextJob();
        return current?.completedSteps
            && current?.completedSteps.some((step) => step === stepName);
    },

    
    isBeforeLastPurchaseFlowStepVisited(stepName, submission) {
        let flowSteps;
        const nextJob = multiLobsData.nextJob();
        const lastStepName = nextJob ? nextJob.lastVisitedStep : undefined;
        if (submission.isPersonalAuto()) {
            flowSteps = paGpaPurchaseFlowSteps.concat(...commonGpaPurchaseFlowSteps);
        } else if (submission.isHomeOwners()) {
            flowSteps = hoGpaPurchaseFlowSteps.concat(...commonGpaPurchaseFlowSteps);
        } else if (submission.isPersonalUmbrella()) {
            flowSteps = pupGpaPurchaseFlowSteps.concat(...commonGpaPurchaseFlowSteps);
        }
        if (flowSteps && lastStepName) {
            return flowSteps.indexOf(stepName) < flowSteps.indexOf(lastStepName);
        }
        return false;
    },

    hasMultiJobs() {
        return multiLobsData.hasMultiJobs();
    }
};
